import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
    fetchAdmissionConfig,
    startApplication,
    verifyPayment,
    buildPublicApplyReturnUrl,
} from '../utils/publicAdmissionApi';
import { buildApiUrl } from '../config/api';
import './admissionStyles.css';

const emptyForm = {
    lastname: '',
    othernames: '',
    gender: '',
    dob: '',
    hometown: '',
    residence: '',
    religion: '',
    classApplied: '',
    guardianRelationship: '',
    guardianlastname: '',
    guardianothernames: '',
    guardianoccupation: '',
    guardiancontact: '',
    guardianemail: '',
    guardianresidence: '',
    motherlastname: '',
    motherothernames: '',
    mothercontact: '',
    fatherlastname: '',
    fatherothernames: '',
    fathercontact: '',
    applicantEmail: '',
};

export default function PublicApplyPage() {
    const { schoolID } = useParams();
    const [searchParams] = useSearchParams();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [form, setForm] = useState(emptyForm);

    const selectedFee = useMemo(() => {
        if (!config || !form.classApplied) return null;
        const cls = config.classes.find((c) => c.classname === form.classApplied);
        return cls ? cls.feeGhs : null;
    }, [config, form.classApplied]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const data = await fetchAdmissionConfig(schoolID);
                if (!cancelled) setConfig(data);
            } catch (e) {
                if (!cancelled) setError(e.message || 'Failed to load');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [schoolID]);

    useEffect(() => {
        const ref = searchParams.get('reference') || searchParams.get('trxref');
        if (!ref) return;
        let cancelled = false;
        (async () => {
            setSubmitting(true);
            setError('');
            try {
                const result = await verifyPayment(ref);
                if (cancelled) return;
                const app = result.application;
                setSuccess(
                    app
                        ? `Payment received. Application ${app.applicationNumber} submitted successfully.`
                        : 'Payment verified.'
                );
                const clean = `#/apply/${encodeURIComponent(schoolID)}`;
                window.history.replaceState({}, document.title, `${window.location.pathname}${clean}`);
            } catch (e) {
                if (!cancelled) setError(e.message || 'Verification failed');
            } finally {
                if (!cancelled) setSubmitting(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [searchParams, schoolID]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSubmitting(true);
        try {
            const returnUrl = buildPublicApplyReturnUrl(schoolID);
            const callbackUrl = `${buildApiUrl('/api/admissions/public/paystack-callback')}?redirect=${encodeURIComponent(returnUrl)}`;
            const result = await startApplication({
                schoolID,
                ...form,
                classApplied: form.classApplied,
                callbackUrl,
            });
            if (result.authorization_url) {
                window.location.href = result.authorization_url;
                return;
            }
            setError('Could not start payment');
        } catch (err) {
            setError(err.message || 'Submission failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="public-admission-page">
            <div className="public-admission-header">SIS Revolution — School admission</div>
            <div className="apply-shell">
                {loading && <p>Loading…</p>}

                {!loading && !config && (
                    <>
                        <div className="alert alert-error">{error || 'School not found'}</div>
                        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                            Check that the school ID in the link is correct and the API server is running.
                        </p>
                    </>
                )}

                {!loading && config && (
                    <>
                        <h1>{config.schoolname || 'School admission'}</h1>
                        <p className="apply-lede">
                            Online application
                            {config.academicYear ? ` · ${config.academicYear}` : ''}
                        </p>

                        {!config.admissionsOpen && (
                            <div className="alert alert-info">
                                Admissions are not open at this time. Please check back later.
                            </div>
                        )}

                        {error && <div className="alert alert-error">{error}</div>}
                        {success && <div className="alert alert-success">{success}</div>}

                        {config.admissionsOpen && config.classes.length === 0 && (
                            <div className="alert alert-info">
                                No classes are accepting applications yet. The school must add an Admission Fee charge on
                                each class.
                            </div>
                        )}

                        {config.admissionsOpen && config.classes.length > 0 && (
                            <form className="apply-form" onSubmit={onSubmit}>
                                {selectedFee != null && (
                                    <div className="fee-banner">
                                        Application fee for <strong>{form.classApplied}</strong>:{' '}
                                        <strong>GHS {Number(selectedFee).toFixed(2)}</strong>
                                        <div style={{ fontSize: '0.85rem', marginTop: '0.35rem', color: '#445' }}>
                                            Non-refundable application fee — payment is required to submit.
                                        </div>
                                    </div>
                                )}

                                <section className="form-section">
                                    <h2>Student</h2>
                                    <div className="form-grid">
                                        <label>
                                            Surname *
                                            <input name="lastname" value={form.lastname} onChange={onChange} required />
                                        </label>
                                        <label>
                                            Other name(s) *
                                            <input name="othernames" value={form.othernames} onChange={onChange} required />
                                        </label>
                                        <label>
                                            Gender
                                            <select name="gender" value={form.gender} onChange={onChange}>
                                                <option value="">Select</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                            </select>
                                        </label>
                                        <label>
                                            Date of birth
                                            <input name="dob" type="date" value={form.dob} onChange={onChange} />
                                        </label>
                                        <label>
                                            Class applying for *
                                            <select
                                                name="classApplied"
                                                value={form.classApplied}
                                                onChange={onChange}
                                                required
                                            >
                                                <option value="">Select class</option>
                                                {config.classes.map((c) => (
                                                    <option key={c.classname} value={c.classname}>
                                                        {c.classname} — GHS {Number(c.feeGhs).toFixed(2)}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>
                                            Hometown
                                            <input name="hometown" value={form.hometown} onChange={onChange} />
                                        </label>
                                        <label>
                                            Residence
                                            <input name="residence" value={form.residence} onChange={onChange} />
                                        </label>
                                        <label>
                                            Religion
                                            <input name="religion" value={form.religion} onChange={onChange} />
                                        </label>
                                    </div>
                                </section>

                                <section className="form-section">
                                    <h2>Guardian</h2>
                                    <div className="form-grid">
                                        <label>
                                            Relationship
                                            <select
                                                name="guardianRelationship"
                                                value={form.guardianRelationship}
                                                onChange={onChange}
                                            >
                                                <option value="">Select</option>
                                                <option value="Mother">Mother</option>
                                                <option value="Father">Father</option>
                                                <option value="Parents">Parents</option>
                                                <option value="Others">Others</option>
                                            </select>
                                        </label>
                                        <label>
                                            Guardian surname
                                            <input
                                                name="guardianlastname"
                                                value={form.guardianlastname}
                                                onChange={onChange}
                                            />
                                        </label>
                                        <label>
                                            Guardian other name(s)
                                            <input
                                                name="guardianothernames"
                                                value={form.guardianothernames}
                                                onChange={onChange}
                                            />
                                        </label>
                                        <label>
                                            Phone *
                                            <input
                                                name="guardiancontact"
                                                value={form.guardiancontact}
                                                onChange={onChange}
                                                required
                                                placeholder="+233..."
                                            />
                                        </label>
                                        <label>
                                            Email
                                            <input
                                                name="guardianemail"
                                                type="email"
                                                value={form.guardianemail}
                                                onChange={onChange}
                                            />
                                        </label>
                                        <label>
                                            Your email (for Paystack receipt)
                                            <input
                                                name="applicantEmail"
                                                type="email"
                                                value={form.applicantEmail}
                                                onChange={onChange}
                                            />
                                        </label>
                                    </div>
                                </section>

                                <section className="form-section">
                                    <h2>Parents (optional)</h2>
                                    <div className="form-grid">
                                        <label>
                                            Mother surname
                                            <input
                                                name="motherlastname"
                                                value={form.motherlastname}
                                                onChange={onChange}
                                            />
                                        </label>
                                        <label>
                                            Mother other names
                                            <input
                                                name="motherothernames"
                                                value={form.motherothernames}
                                                onChange={onChange}
                                            />
                                        </label>
                                        <label>
                                            Mother phone
                                            <input name="mothercontact" value={form.mothercontact} onChange={onChange} />
                                        </label>
                                        <label>
                                            Father surname
                                            <input name="fatherlastname" value={form.fatherlastname} onChange={onChange} />
                                        </label>
                                        <label>
                                            Father other names
                                            <input
                                                name="fatherothernames"
                                                value={form.fatherothernames}
                                                onChange={onChange}
                                            />
                                        </label>
                                        <label>
                                            Father phone
                                            <input name="fathercontact" value={form.fathercontact} onChange={onChange} />
                                        </label>
                                    </div>
                                </section>

                                <div className="apply-actions">
                                    <button type="submit" className="btn-primary" disabled={submitting || !form.classApplied}>
                                        {submitting ? 'Redirecting to payment…' : 'Pay & submit application'}
                                    </button>
                                    <Link
                                        to={`/apply/${encodeURIComponent(schoolID)}/status`}
                                        className="btn-secondary"
                                    >
                                        Track application
                                    </Link>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
